import React, {useCallback} from 'react';

import {useTranslation} from 'react-i18next';

import {
    doUpdateEmployee,
    resendEmployeeInvitation,
    resetEmployeePassword,
} from '@/entities/eployersControl/model';
import {Employee, UserStatus} from '@/entities/eployersControl/type';
import {DropdownMenu} from '@/shared/ui/DropdownMenu';

const GEN_INFO = 'page_employers_control.control';

const DropdownMenuItems = ({
    row,
    userControlModal,
    status,
}: {
    row?: Employee;
    userControlModal?: () => void;
    status: UserStatus;
}) => {
    const {t: translation} = useTranslation();
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});

    const onResetPassword = useCallback(() => {
        if (!row) {
            return;
        }
        resetEmployeePassword({
            firstName: row.firstName,
            userName: row.email,
            email: row.email,
        });
    }, [row]);

    const onResendInvitation = useCallback(() => {
        if (!row) {
            return;
        }
        resendEmployeeInvitation({
            firstName: row.firstName,
            userName: row.email,
            email: row.email,
        });
    }, [row]);

    const onEnableAccess = useCallback(
        ({userActive}: {userActive: boolean}) => {
            if (row) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const {isActive, taxAuthorityDepartment, ...rest} = row;
                const bodyToUpdate = {
                    ...rest,
                    active: userActive,
                };

                doUpdateEmployee({
                    userId: row.id,
                    body: bodyToUpdate,
                });
            }
        },
        [row],
    );

    return (
        <DropdownMenu.List style={{zIndex: 1200, position: 'relative'}}>
            {status === 'ACTIVE' && (
                <>
                    <DropdownMenu.Item onSelect={() => onEnableAccess({userActive: false})}>
                        {t('disableAccess')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onSelect={onResetPassword}>
                        {t('resetPassword')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onSelect={onResendInvitation}>
                        {t('resendInvitation')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onSelect={() => userControlModal?.()}>
                        {t('edit')}
                    </DropdownMenu.Item>
                </>
            )}
            {status === 'INACTIVE' && (
                <>
                    <DropdownMenu.Item onSelect={() => onEnableAccess({userActive: true})}>
                        {t('enableAccess')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onSelect={onResetPassword}>
                        {t('resetPassword')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onSelect={onResendInvitation}>
                        {t('resendInvitation')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item onSelect={() => userControlModal?.()}>
                        {t('edit')}
                    </DropdownMenu.Item>
                </>
            )}
        </DropdownMenu.List>
    );
};

export const LineControlComponent = ({
    userStatus,
    row,
    userControlModal,
}: {
    userStatus?: UserStatus;
    row?: Employee;
    userControlModal?: () => void;
}) => {
    const bullets = `•••`;
    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <DropdownMenu noIcon={true} label={bullets}>
                {userStatus && (
                    <DropdownMenuItems
                        row={row}
                        userControlModal={userControlModal}
                        status={userStatus}
                    />
                )}
            </DropdownMenu>
        </div>
    );
};
